// Regras de habilitação dos botões de integração da tabela de frete na tela de Validar Lotes (RN-29 e RN-30).
// Função pura, sem React: espelha a trava de ATIVO da API, que continua sendo a autoridade.

// Pagador/Tomador, Remetente, Local de Coleta e Destinatário chegam do back já formatados como
// "código - documento - nome", ou "N/A" quando a relação não existe (ver LotesService.findOneToValidarLote).
// Local de Entrega fica de fora de propósito: o próprio payload de referência da Rodopar manda esse
// campo em branco normalmente, então não é um pré-requisito real pra criar a PC.
export interface LoteComDocumentosPC {
  pagadorTomador?: string | null
  remetente?: string | null
  localColeta?: string | null
  destinatario?: string | null
}

export interface LoteParaAcoes extends LoteComDocumentosPC {
  tipoContratacao: string
  codigoTabelaFrete?: string | null
  numeroPC?: string | null
  statusAtivado: Date | string | null
  reajustePendente?: boolean
  vinculoTabelaClienteIntegrado?: boolean
  Pernas?: { idRodifr?: number | null; idRodiif?: number | null }[]
}

export interface EstadoAcao {
  visivel: boolean
  habilitado: boolean
  // Explicação exibida em tooltip quando o botão está desabilitado
  motivo?: string
}

export interface EstadoAcoes {
  criarTabelaFrete: EstadoAcao
  criarPC: EstadoAcao
  criarTabelaCliente: EstadoAcao
  reajuste: EstadoAcao
  reprocessarCenarios: EstadoAcao
  podeAtivar: boolean
  motivoAtivacaoBloqueada?: string
}

// A Rodopar pode gravar o cabeçalho da tabela (CODFRE) e rejeitar o itinerário (RODIFR/RODIIF); nesse
// caso os IDs do itinerário nunca chegam na perna. Tabela criada com itinerário rejeitado = com erro.
export function tabelaComErroItinerario(
  lote: Pick<LoteParaAcoes, 'codigoTabelaFrete' | 'Pernas'>
): boolean {
  const perna = lote.Pernas?.[0]
  const itinerarioOk = !!perna?.idRodifr && !!perna?.idRodiif

  return !!lote.codigoTabelaFrete && !itinerarioOk
}

export const temPC = (numeroPC?: string | null) =>
  !!numeroPC && numeroPC !== '0'

const campoPreenchido = (valor?: string | null) => !!valor && valor !== 'N/A'

const CAMPOS_DOCUMENTO_PC: {
  campo: keyof LoteComDocumentosPC
  label: string
}[] = [
  { campo: 'pagadorTomador', label: 'Pagador/Tomador' },
  { campo: 'remetente', label: 'Remetente' },
  { campo: 'localColeta', label: 'Local de Coleta' },
  { campo: 'destinatario', label: 'Destinatário' }
]

// A Rodopar valida esses 4 campos contra o cadastro dela e recusa a PC com uma mensagem genérica de
// log quando faltam — melhor travar o botão aqui, com o campo indicado, do que deixar a Rodopar recusar.
export function camposDocumentoPCFaltando(lote: LoteComDocumentosPC): string[] {
  return CAMPOS_DOCUMENTO_PC.filter(
    ({ campo }) => !campoPreenchido(lote[campo])
  ).map(({ label }) => label)
}

export function resolverEstadoAcoes(
  lote: LoteParaAcoes,
  emValidacao: boolean
): EstadoAcoes {
  const isSpot = lote.tipoContratacao === 'SPOT'
  const visivel = emValidacao && isSpot
  const jaAtivado = lote.statusAtivado != null
  const hasTabela = !!lote.codigoTabelaFrete
  const hasPC = temPC(lote.numeroPC)
  const vinculado = !!lote.vinculoTabelaClienteIntegrado

  const camposFaltando = camposDocumentoPCFaltando(lote)

  const criarPC: EstadoAcao = { visivel, habilitado: true }
  if (!hasTabela) {
    criarPC.habilitado = false
    criarPC.motivo = 'Crie a tabela de frete antes de criar a PC.'
  } else if (hasPC) {
    criarPC.habilitado = false
    criarPC.motivo = 'A PC já foi criada.'
  } else if (camposFaltando.length > 0) {
    criarPC.habilitado = false
    criarPC.motivo = `Preencha no lote antes de criar a PC: ${camposFaltando.join(
      ', '
    )}.`
  }

  const criarTabelaCliente: EstadoAcao = { visivel, habilitado: true }
  if (!hasPC) {
    criarTabelaCliente.habilitado = false
    criarTabelaCliente.motivo = 'Crie a PC antes de criar a tabela por cliente.'
  } else if (vinculado) {
    criarTabelaCliente.habilitado = false
    criarTabelaCliente.motivo = 'A tabela por cliente já foi criada.'
  }

  // Só cria quando ainda não há tabela ou quando ela foi criada com erro no itinerário. Com a tabela
  // íntegra, o que resta é o Reajuste de Valores (ação diferente: AJUSTE_RODFRA, só valores).
  const criarTabelaFrete: EstadoAcao = { visivel, habilitado: true }
  if (hasTabela && !tabelaComErroItinerario(lote)) {
    criarTabelaFrete.habilitado = false
    criarTabelaFrete.motivo =
      'A tabela de frete já foi criada. Para alterar valores use o Reajuste de Valores.'
  }
  // Sem gate de sequência: o motor de resolução não depende do que os outros botões produzem.
  const reprocessarCenarios: EstadoAcao = { visivel, habilitado: true }

  // Reajuste só depois da primeira ativação e com tabela existente
  const reajuste: EstadoAcao = {
    visivel: visivel && hasTabela && jaAtivado,
    habilitado: true
  }

  // Só o lote SPOT é travado; CONTRATO ativa como antes.
  let motivoAtivacaoBloqueada: string | undefined
  if (isSpot) {
    if (!hasTabela) {
      motivoAtivacaoBloqueada = 'Crie a tabela de frete antes de liberar.'
    } else if (!hasPC) {
      motivoAtivacaoBloqueada = 'Crie a PC antes de liberar.'
    } else if (!vinculado) {
      motivoAtivacaoBloqueada = 'Crie a tabela por cliente antes de liberar.'
    } else if (lote.reajustePendente) {
      motivoAtivacaoBloqueada =
        'Há reajuste de valores pendente. Envie o Reajuste de Valores antes de liberar.'
    }
  }

  return {
    criarTabelaFrete,
    criarPC,
    criarTabelaCliente,
    reajuste,
    reprocessarCenarios,
    podeAtivar: !motivoAtivacaoBloqueada,
    motivoAtivacaoBloqueada
  }
}
